import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  BarChart3, Download, Table2, FileText, CheckCircle, X, Loader2, Info,
  ChevronDown, FileSpreadsheet, FileBadge, Settings
} from 'lucide-react';
import api from '@/utils/api';
import AnalyticsFilters from '@/components/analytics/AnalyticsFilters';
import AnalyticsKPIs from '@/components/analytics/AnalyticsKPIs';
import PipelineForecast from '@/components/analytics/PipelineForecast';
import BreakdownCharts from '@/components/analytics/BreakdownCharts';
import TrendCharts from '@/components/analytics/TrendCharts';
import RecentActivityMatrix from '@/components/analytics/RecentActivityMatrix';
import USStateHeatmap from '@/components/analytics/USStateHeatmap';
import ExportCustomizationModal from '@/components/analytics/ExportCustomizationModal';
import StateMapDrillDown from '@/components/analytics/StateMapDrillDown';
import PdfViewerModal from '@/components/PdfViewerModal';
import { STATE_NAME_MAP } from '@/components/analytics/analyticsHelpers.jsx';
import { AnalyticsSkeleton } from '@/components/LoadingIndicators';

export default function Analytics() {
  // Initialize with empty state structure showing 0s
  const emptyAnalytics = {
    total_proposals: 0,
    total_revenue: 0,
    total_gross_revenue: 0,
    total_net_revenue: 0,
    avg_deal_size: 0,
    approval_rate: 0,
    avg_approval_time: 0,
    pipeline_health: { score: 0, status: 'Not Started' },
    top_services: [],
    top_states: [],
    filter_options: { account_executives: [], services: [], states: [] },
    summary: {
      total_scenarios: 0,
      total_collections: 0,
      total_gross_revenue: 0,
      total_net_revenue: 0,
      approval_rate: 0,
      avg_approval_time: 0
    },
    status_breakdown: [],
    service_breakdown: [],
    account_executive_breakdown: [],
    state_breakdown: [],
    recent_activity: [],
    detailed_scenarios: []
  };
  
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [trendData, setTrendData] = useState({ trend_data: [] });
  const [matrixData, setMatrixData] = useState({ service_matrix: [] });
  const [heatmapData, setHeatmapData] = useState({ state_data: [], city_data: [] });
  const [loading, setLoading] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  // Default to showing all meaningful statuses for both scenarios AND collections
  // Includes scenario statuses (pending_approval, approved, rejected, etc.)
  // AND collection statuses (partially_approved, fully_approved, fully_denied)
  const [filters, setFilters] = useState({ 
    period: 'custom', 
    status: 'submitted,pending_approval,pending_cfo_approval,pending_ceo_approval,pending_admin_approval,approved,rejected,partially_approved,fully_approved,fully_denied',
    serviceId: '', 
    accountExecutive: '', 
    state: '', 
    startDate: '', 
    endDate: '' 
  });
  const [filterOptions, setFilterOptions] = useState({ account_executives: [], services: [], states: [] });
  const [userRole, setUserRole] = useState('preparer');
  const [expandedSections, setExpandedSections] = useState({ trends: true, matrix: true, heatmap: true, detailed: false });
  const [trendInterval, setTrendInterval] = useState('monthly');
  const [drillDownState, setDrillDownState] = useState(null);
  const [municipalityData, setMunicipalityData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [showDetailedTable, setShowDetailedTable] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState(null);
  const [donutChartView, setDonutChartView] = useState('gross');
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: null, blob: null, filename: '' });

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: null, blob: null, filename: '' });
  };

  const toggleSection = (section) => setExpandedSections(p => ({ ...p, [section]: !p[section] }));

  const buildQueryParams = () => {
    const params = {};
    if (filters.period && filters.period !== 'custom') params.period = filters.period;
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
    if (filters.serviceId) params.service_id = filters.serviceId;
    if (filters.accountExecutive) params.account_executive = filters.accountExecutive;
    if (filters.state) params.state = filters.state;
    if (filters.status) params.status = filters.status; // comma-separated string passed directly
    return params;
  };

  const fetchAnalytics = async () => {
    try {
      console.log('[Analytics] fetchAnalytics called');
      setLoading(true);
      const params = buildQueryParams();
      const [dashRes, trendRes, matrixRes, geoRes] = await Promise.all([
        api.get('/analytics/dashboard', { params }),
        api.get('/analytics/trends', { params: { ...params, interval: trendInterval } }),
        api.get('/analytics/matrix', { params }),
        api.get('/analytics/geographic', { params }),
      ]);
      setAnalytics(dashRes.data);
      setTrendData(trendRes.data);
      setMatrixData(matrixRes.data);
      setHeatmapData(geoRes.data);
      if (dashRes.data.filter_options) setFilterOptions(dashRes.data.filter_options);
      setHasLoadedData(true);
      console.log('[Analytics] Data loaded successfully. hasLoadedData set to true');
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Initialize on mount - NO data fetching
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role || 'preparer');
    // Do NOT fetch any data on mount - user must click Apply to load data
    console.log('[Analytics] Component mounted. hasLoadedData:', false);
    console.log('[Analytics] Initial analytics state:', emptyAnalytics);
  }, []);

  // Reload analytics data when returning from Settings/Manager pages
  // Only reload if data was already loaded (user had applied filters)
  useEffect(() => {
    // Check on mount
    const shouldReload = localStorage.getItem('reload_reference_data');
    if (shouldReload && hasLoadedData) {
      console.log('[Analytics] Reload flag detected on mount - refreshing data');
      localStorage.removeItem('reload_reference_data');
      fetchAnalytics();
    }

    // Handle storage events (cross-tab/window communication)
    const handleStorageChange = (e) => {
      if (e.key === 'reload_reference_data' && hasLoadedData) {
        console.log('[Analytics] Storage event detected - refreshing data');
        fetchAnalytics();
        localStorage.removeItem('reload_reference_data');
      }
    };

    // Handle window focus (user returns to this tab/window)
    const handleFocus = () => {
      const shouldReload = localStorage.getItem('reload_reference_data');
      if (shouldReload && hasLoadedData) {
        console.log('[Analytics] Focus detected with reload flag - refreshing data');
        fetchAnalytics();
        localStorage.removeItem('reload_reference_data');
      }
    };

    // Handle visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const shouldReload = localStorage.getItem('reload_reference_data');
        if (shouldReload && hasLoadedData) {
          console.log('[Analytics] Page became visible with reload flag - refreshing data');
          fetchAnalytics();
          localStorage.removeItem('reload_reference_data');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasLoadedData]);

  const handleFilterChange = (key, value) => setFilters(p => ({ ...p, [key]: value }));
  const applyFilters = () => fetchAnalytics();
  const clearFilters = () => {
    setFilters({ period: 'custom', status: '', serviceId: '', accountExecutive: '', state: '', startDate: '', endDate: '' });
    // Reset to empty state when clearing filters
    setAnalytics(emptyAnalytics);
    setTrendData({ trend_data: [] });
    setMatrixData({ service_matrix: [] });
    setHeatmapData({ state_data: [], city_data: [] });
    setHasLoadedData(false);
  };

  const handleDrillDown = (type, value) => {
    setDrillDownFilter({ type, value });
    setShowDetailedTable(true);
    setExpandedSections(p => ({ ...p, detailed: true }));
  };

  const handleStateDrillDown = async (stateCode) => {
    try {
      setDrillDownLoading(true);
      const params = buildQueryParams();
      params.drill_state = stateCode;
      const res = await api.get('/analytics/geographic', { params });
      setDrillDownState(stateCode);
      setMunicipalityData(res.data);
    } catch (err) {
      console.error('Error fetching state drill-down:', err);
      toast.error('Failed to load state details');
    } finally {
      setDrillDownLoading(false);
    }
  };
  const closeDrillDown = () => { setDrillDownState(null); setMunicipalityData(null); };

  const exportMatrixCSV = async () => {
    if (!matrixData?.matrix_data) return;
    const headers = ['Service','Scenarios','Gross Revenue Sum','Gross Revenue Avg','Net Revenue Sum','Net Revenue Avg','Total Payments Sum','Total Payments Avg','Implied Take Rate Avg','Approval Rate'];
    const rows = matrixData.matrix_data.map(r => [r.service_name,r.scenario_count,r.gross_revenue_sum,r.gross_revenue_avg,r.net_revenue_sum,r.net_revenue_avg,r.total_payments_sum,r.total_payments_avg,r.implied_take_rate_avg,r.approval_rate].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const { triggerBlobDownload } = await import('@/utils/downloadHelper');
    triggerBlobDownload(blob, 'matrix_report.csv');
    toast.success('Matrix exported to CSV');
  };

  const exportData = async (format, selectedColumns = null) => {
    try {
      setExporting(true);
      toast.info(`Generating ${format.toUpperCase()} export...`);
      const { triggerBlobDownload } = await import('@/utils/downloadHelper');
      if (format === 'excel' || format === 'pdf') {
        const endpoint = format === 'excel' ? 'export/excel' : 'export/pdf';
        const ext = format === 'excel' ? 'xlsx' : 'pdf';
        const mimeType = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
        // Pass all current filters so export matches what's shown on screen
        const exportPayload = {
          columns: selectedColumns,
          startDate: filters.startDate,
          endDate: filters.endDate,
          status: filters.status,
          serviceId: filters.serviceId,
          accountExecutive: filters.accountExecutive,
          state: filters.state,
        };
        const res = await api.post(`/analytics/${endpoint}`, exportPayload, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: mimeType });
        if (format === 'pdf') {
          const blobUrl = URL.createObjectURL(blob);
          setPdfViewer({ show: true, url: blobUrl, blob, filename: `analytics_report.pdf` });
        } else {
          triggerBlobDownload(blob, `analytics_report.${ext}`);
        }
        toast.success(`Report downloaded as ${format.toUpperCase()}`);
        return;
      }
      // CSV export — pass all current filters as query params
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.serviceId) params.append('service_id', filters.serviceId);
      const res = await api.get(`/analytics/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      triggerBlobDownload(blob, `analytics_report.csv`);
      toast.success('Report downloaded as CSV');
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const prepareDonutChartData = () => {
    if (!analytics?.detailed_scenarios) return [];
    const serviceMap = {};
    analytics.detailed_scenarios.forEach(s => {
      const name = s.service_name || 'Unknown';
      const grossRevenue = s.total_gross_revenue || s.gross_revenue || 0;
      const netRevenue = s.total_net_revenue || s.net_revenue || 0;
      if (!serviceMap[name]) serviceMap[name] = { gross: 0, net: 0 };
      serviceMap[name].gross += grossRevenue;
      serviceMap[name].net += netRevenue;
    });
    return Object.entries(serviceMap).filter(([, d]) => d.gross > 0 || d.net > 0).map(([name, d]) => ({
      name, value: donutChartView === 'gross' ? d.gross : d.net
    }));
  };
  const donutChartData = prepareDonutChartData();

  const filteredDetailedData = useMemo(() => {
    if (!analytics?.detailed_scenarios) return [];
    let filtered = analytics.detailed_scenarios;
    if (drillDownFilter) {
      switch (drillDownFilter.type) {
        case 'status': filtered = filtered.filter(s => s.status === drillDownFilter.value); break;
        case 'service': filtered = filtered.filter(s => s.service_name === drillDownFilter.value); break;
        case 'account_executive': filtered = filtered.filter(s => s.creator_name === drillDownFilter.value); break;
        case 'state': filtered = filtered.filter(s => s.municipality?.includes(drillDownFilter.value)); break;
        default: break;
      }
    }
    return filtered;
  }, [analytics, drillDownFilter]);

  if (loading) {
    return <AnalyticsSkeleton />;
  }

  const { summary, status_breakdown, service_breakdown, account_executive_breakdown, state_breakdown, recent_activity } = analytics;

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 max-w-[1400px]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
              <BarChart3 className="w-5 h-5 text-sky-600" />
              Analytics & Reporting
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">Insights, matrix reports, and geographic visualization</p>
            <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 bg-sky-50 text-sky-700 rounded-lg text-xs">
              <CheckCircle className="w-3.5 h-3.5" />
              <span className="font-medium">All Statuses (Including Drafts)</span>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Quick Download Report dropdown */}
            <div className="relative group" data-testid="download-report-dropdown">
              <button
                disabled={exporting || !hasLoadedData}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="download-report-btn"
                onClick={() => {}}
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? 'Exporting...' : 'Download Report'}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20 hidden group-hover:block group-focus-within:block">
                <button
                  onClick={() => exportData('excel')}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 rounded-t-lg"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  Excel (.xlsx)
                </button>
                <button
                  onClick={() => exportData('csv')}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                  data-testid="export-csv-btn"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  CSV Data
                </button>
                <button
                  onClick={() => exportData('pdf')}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 rounded-b-lg"
                  data-testid="export-pdf-btn"
                >
                  <FileBadge className="w-4 h-4 text-red-500" />
                  PDF Report
                </button>
              </div>
            </div>
            <button onClick={() => setShowExportModal(true)} disabled={exporting || !hasLoadedData} className="btn-secondary inline-flex items-center gap-2" data-testid="customize-export-btn">
              <Settings className="w-4 h-4" />
              Customize
            </button>
            {matrixData && hasLoadedData && <button onClick={exportMatrixCSV} className="btn-secondary inline-flex items-center gap-2" data-testid="export-matrix-btn"><Table2 className="w-4 h-4" /> Matrix CSV</button>}
          </div>
        </div>
        
        {/* Analytics Quick Guide */}
        <div className="mt-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-l-4 border-indigo-500 p-3 rounded-r-lg">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-indigo-800">
                <strong>💡 Tip:</strong> Use filters below to narrow down data → Click charts/states for drill-downs → Export reports via the buttons above
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnalyticsFilters filters={filters} onFilterChange={handleFilterChange} onApplyFilters={applyFilters} onClearFilters={clearFilters} filterOptions={filterOptions} userRole={userRole} />
      
      {/* Show helpful message when no data loaded */}
      {!hasLoadedData && (
        <div className="max-w-[1400px] bg-white rounded-lg border border-slate-200 p-8 text-center mb-6">
          <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-sky-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Ready to View Analytics</h3>
          <p className="text-slate-600 mb-4">Select your filter parameters above and click <strong>"Apply"</strong> to load analytics data.</p>
          <p className="text-sm text-slate-500">All KPI cards and charts will display data based on your selected filters.</p>
        </div>
      )}
      
      {hasLoadedData && (
        <>
          <AnalyticsKPIs summary={summary} />
          <PipelineForecast analytics={analytics} />
          <BreakdownCharts analytics={analytics} summary={summary} status_breakdown={status_breakdown} service_breakdown={service_breakdown} account_executive_breakdown={account_executive_breakdown} state_breakdown={state_breakdown} donutChartData={donutChartData} donutChartView={donutChartView} setDonutChartView={setDonutChartView} handleDrillDown={handleDrillDown} />
          <TrendCharts trendData={trendData} trendInterval={trendInterval} setTrendInterval={setTrendInterval} fetchAnalytics={fetchAnalytics} expandedSections={expandedSections} toggleSection={toggleSection} />
          <RecentActivityMatrix analytics={analytics} recent_activity={recent_activity} matrixData={matrixData} expandedSections={expandedSections} toggleSection={toggleSection} handleDrillDown={handleDrillDown} exportMatrixCSV={exportMatrixCSV} showDetailedTable={showDetailedTable} filteredDetailedData={filteredDetailedData} />
          <USStateHeatmap heatmapData={heatmapData} expandedSections={expandedSections} toggleSection={toggleSection} onStateDrillDown={handleStateDrillDown} />
        </>
      )}

      {!showDetailedTable && hasLoadedData && analytics.detailed_scenarios?.length > 0 && (
        <div className="mt-6 text-center">
          <button onClick={() => { setShowDetailedTable(true); setDrillDownFilter(null); setExpandedSections(p => ({ ...p, detailed: true })); }} className="btn-primary inline-flex items-center gap-2">
            <FileText className="w-5 h-5" /> View All Detailed Scenario Data ({analytics.detailed_scenarios.length} total)
          </button>
        </div>
      )}

      {drillDownState && municipalityData && (
        <StateMapDrillDown stateCode={drillDownState} stateName={STATE_NAME_MAP[drillDownState] || drillDownState} municipalityData={municipalityData} onClose={closeDrillDown} />
      )}
      <ExportCustomizationModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} onExport={exportData} />

      {pdfViewer.show && (
        <PdfViewerModal
          url={pdfViewer.url}
          name="Analytics Export"
          blob={pdfViewer.blob}
          filename={pdfViewer.filename}
          onClose={closePdfViewer}
        />
      )}
    </div>
  );
}
